import * as React from "react";
import { inject } from "mobx-react";
import { decorateDestinationHeroHtml, Sources } from "../DestinationHeroUtilities";
import { WizardHeroProps } from "../typings";
import { Media } from "typings/microserviceModels/destination-hero-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { useLocalization } from "@shared-ui/localization-context";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";

export const WizardHero = inject("thumbor")((props: WizardHeroProps) => {
  const { flexComponent, context, thumbor } = props;
  const { model } = flexComponent;
  const { content } = model;
  const { formatText } = useLocalization();
  const isWizardRedesignVariantActive = context && isVariantEnabled(context, Experiment.Flex_Blossom_Wizard_Redesign);

  const firstImage = content && content.media && content.media[0];
  const fallbackImage: Media = {
    // Empty fields are required to adhere to our autogenerated typing for Media.
    cardIdentifier: "",
    mediaUrl: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaUrlTrimmed: "",
    mediaUrlOriginal: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaAlt: "Default HTG hotel image",
    origin: "Expedia",
    source: "",
    author: "",
    authorUrl: "",
    license: { name: "", suite: "", url: "" },
    mediaType: "Image",
  };
  const selectedHeroImage =
    firstImage && !firstImage.mediaUrl.includes("TE_Clouds_card_r1.jpg") ? firstImage : fallbackImage;

  const thumborizedHeroImageUrl = (): string => {
    const { mediaUrl } = selectedHeroImage;
    if (!thumbor) {
      return mediaUrl;
    }

    const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

    return isMobile
      ? thumbor.safelyGenerateThumborizedImageUrl(mediaUrl, 360, 224, context, AspectRatioEnum.DEFAULT, true, 80)
      : thumbor.safelyGenerateThumborizedImageUrl(mediaUrl, 1040, 580, context, AspectRatioEnum.DEFAULT, false, 100);
  };
  const wizardHeroGradient = !isWizardRedesignVariantActive
    ? "linear-gradient(rgba(0, 0, 0, 0.56), rgba(0, 0, 0, 0.56)),"
    : "";
  const backgroundImage = `${wizardHeroGradient} url('${thumborizedHeroImageUrl()}')`;
  const { source } = selectedHeroImage;
  const isTroverSource = source === Sources.Trover;
  const isWikimediaSource = source === Sources.Wikimedia;
  const isAttributionRequired = isTroverSource || isWikimediaSource;
  const attributionClass = isAttributionRequired ? "withAttributionText" : "";
  const wizardHeroImageOnly = isWizardRedesignVariantActive ? "wizardImage" : "wizard";
  const mainClassNames = `DestinationHero ${wizardHeroImageOnly} uitk-cell all-cell-1-1 ${attributionClass}`;

  const heroTitle = (): string => {
    if (!isWikimediaSource) {
      return "";
    }

    return formatText(
      "hero.attribution.wikimediaAlt",
      selectedHeroImage.mediaAlt,
      selectedHeroImage.author,
      selectedHeroImage.license && selectedHeroImage.license.name
    );
  };

  const attributionText = (): React.ReactElement | null => {
    if (!isAttributionRequired) {
      return null;
    }

    const { mediaUrlOriginal, author, authorUrl, license } = selectedHeroImage;
    const licenseSuite = license && license.suite;
    const licenseUrl = license && license.url;

    const htmlToDisplay = () => {
      if (isTroverSource) {
        const troverPhotoAnchor = formatText("hero.attribution.troverLink", author, mediaUrlOriginal);

        return decorateDestinationHeroHtml(troverPhotoAnchor);
      } else {
        const wikimediaPhotoAnchor = formatText(
          "hero.attribution.wikimediaLink",
          mediaUrlOriginal,
          authorUrl,
          author,
          licenseUrl,
          licenseSuite
        );

        return decorateDestinationHeroHtml(wikimediaPhotoAnchor);
      }
    };

    return (
      <span className="uitk-type-200 all-x-padding-three all-y-padding-two" dangerouslySetInnerHTML={htmlToDisplay()} />
    );
  };

  return (
    <>
      <div
        className={mainClassNames}
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        style={{ backgroundImage }}
        title={heroTitle()}
      />
      {attributionText()}
    </>
  );
});
